import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Gift, Copy, Share2, Trophy, Sparkles, Star, MailWarning, Check, Lock, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

function TierRow({ t, count, i }) {
  const pct = Math.min(100, (count / t.threshold) * 100);
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                data-testid={`referral-tier-${t.threshold}`}
                className={`relative rounded-2xl border p-4 overflow-hidden ${t.earned ? 'border-chartreuse/50 bg-chartreuse/10' : 'border-border bg-background/40'}`}>
      <div className="flex items-center gap-2">
        <span className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${t.earned ? 'bg-chartreuse text-obsidian' : 'bg-secondary text-muted-foreground'}`}>
          {t.earned ? <Check size={15}/> : <Lock size={13}/>}
        </span>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{t.threshold} {t.threshold === 1 ? 'invito' : 'inviti'}</div>
          <div className="font-medium text-sm truncate">{t.label}</div>
        </div>
      </div>
      <ul className="mt-3 space-y-1 text-xs">
        {t.rewards.map(r => <li key={r} className="flex gap-1.5"><Star size={11} className="text-chartreuse shrink-0 mt-0.5"/>{r}</li>)}
      </ul>
      {!t.earned && (
        <div className="mt-3 h-1 rounded-full bg-secondary overflow-hidden">
          <motion.div className="h-full bg-chartreuse" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }}/>
        </div>
      )}
    </motion.div>
  );
}

function CreditsPanel({ credits, mine, onUse }) {
  const free = credits.filter(c => !c.used_on);
  const [pick, setPick] = useState(null);
  if (!credits.length) return null;
  return (
    <div className="mt-6" data-testid="referral-credits">
      <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-2">I tuoi crediti "In evidenza"</div>
      <div className="flex flex-wrap gap-2">
        {credits.map(c => (
          <div key={c.credit_id} className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${c.used_on ? 'border-border text-muted-foreground' : 'border-chartreuse/50 bg-chartreuse/10'}`}>
            <Sparkles size={12} className={c.used_on ? '' : 'text-chartreuse'}/> {c.days} giorni
            {c.used_on ? <span>· usato</span> : (
              <button onClick={() => setPick(c)} className="font-semibold underline-offset-2 hover:underline" data-testid={`referral-use-credit-${c.credit_id}`}>Usa</button>
            )}
          </div>
        ))}
      </div>
      <Dialog open={!!pick} onOpenChange={(o) => !o && setPick(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Metti in evidenza un annuncio</DialogTitle>
            <DialogDescription>Per {pick?.days} giorni il tuo annuncio apparirà in cima ai risultati con l'etichetta "In evidenza".</DialogDescription>
          </DialogHeader>
          {mine.length === 0 ? <div className="text-sm text-muted-foreground">Pubblica prima un terreno per usare il credito.</div> : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {mine.map(l => (
                <button key={l.listing_id} onClick={() => { onUse(pick, l); setPick(null); }} data-testid={`referral-apply-${l.listing_id}`}
                        className="w-full text-left rounded-xl border border-border p-3 hover:border-chartreuse/60 transition-colors">
                  <div className="font-medium text-sm">{l.title}</div>
                  <div className="text-xs text-muted-foreground">{l.location?.region} · {l.featured_until && new Date(l.featured_until) > new Date() ? `già in evidenza fino al ${new Date(l.featured_until).toLocaleDateString('it-IT')} (si somma)` : 'non in evidenza'}</div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {free.length > 0 && <div className="text-[11px] text-muted-foreground mt-2">{free.length} credito/i da usare.</div>}
    </div>
  );
}

export default function ReferralCard({ mine = [] }) {
  const [data, setData] = useState(null);
  const [featuredMap, setFeaturedMap] = useState({});
  const myListings = mine.map(l => (featuredMap[l.listing_id] ? { ...l, featured_until: featuredMap[l.listing_id] } : l));
  const load = () => api.get('/users/me/referral').then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const { code, link, count, pending, tiers, early_access, email_verified, credits } = data;
  const nextTier = tiers.find(t => !t.earned);

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copiato`); }
    catch { toast.error('Impossibile copiare — fallo manualmente'); }
  };
  const share = async () => {
    const shareText = `Ti invito su AgriNexus: compra, affitta o valuta terreni agricoli in Italia e stima gratis il tuo raccolto con l'AI. Iscriviti con il mio codice ${code}: ${link}`;
    if (navigator.share) { try { await navigator.share({ title: 'AgriNexus', text: shareText, url: link }); } catch { /* annullato */ } }
    else copy(shareText, 'Messaggio invito');
  };
  const resend = async () => {
    try { await api.post('/auth/resend-verification'); toast.success('Email di conferma inviata'); } catch (e) { toast.error(formatApiError(e)); }
  };
  const useCredit = async (credit, listing) => {
    try {
      const { data: r } = await api.post(`/users/me/featured-credits/${credit.credit_id}/apply`, { listing_id: listing.listing_id });
      toast.success(`"${listing.title}" in evidenza fino al ${new Date(r.featured_until).toLocaleDateString('it-IT')}`);
      setFeaturedMap(m => ({ ...m, [listing.listing_id]: r.featured_until }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="referral-card" className="rounded-3xl border border-border glass-vp p-6 md:p-8 relative overflow-hidden">
      <div aria-hidden className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-[100px] opacity-25" style={{ background: 'rgb(180,255,0)' }}/>
      <div className="relative flex items-start gap-4 flex-wrap">
        <div className="shrink-0 w-12 h-12 rounded-2xl bg-chartreuse/15 grid place-items-center"><Gift size={22} className="text-chartreuse"/></div>
        <div className="flex-1 min-w-[240px]">
          <h3 className="font-display text-2xl tracking-tight">Codice amico: invita e sblocca premi</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Un invito è valido quando il tuo amico conferma l'email e pubblica un annuncio o fa una stima Yield AI PRO. Ogni scaglione sblocca vantaggi reali.
          </p>

          {!email_verified && (
            <div className="mt-4 flex items-center gap-3 flex-wrap rounded-xl border border-terracotta/40 bg-terracotta/10 p-3 text-sm" data-testid="referral-verify-banner">
              <MailWarning size={16} className="text-terracotta"/> Conferma la tua email per completare il tuo account.
              <button onClick={resend} className="underline font-medium" data-testid="referral-resend-verification">Invia di nuovo</button>
            </div>
          )}

          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <div className="rounded-full border-2 border-dashed border-chartreuse/50 px-5 py-2 font-mono font-bold tracking-[0.3em] text-lg bg-background" data-testid="referral-code">{code}</div>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => copy(code, 'Codice')} data-testid="referral-copy-code"><Copy size={13} className="mr-1.5"/> Copia codice</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => copy(link, 'Link')} data-testid="referral-copy-link"><Copy size={13} className="mr-1.5"/> Copia link</Button>
            <Button size="sm" className="rounded-full bg-chartreuse text-obsidian hover:bg-chartreuse/90" onClick={share} data-testid="referral-share"><Share2 size={13} className="mr-1.5"/> Condividi</Button>
          </div>

          <div className="mt-6 flex items-end gap-8 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Inviti validi</div>
              <div className="font-display text-4xl tracking-tighter" data-testid="referral-count">{count}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1"><Clock size={11}/> In attesa</div>
              <div className="font-display text-4xl tracking-tighter text-muted-foreground" data-testid="referral-pending">{pending}</div>
            </div>
            {nextTier && (
              <div className="sm:ml-auto sm:text-right">
                <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Prossimo premio</div>
                <div className="font-medium text-sm">{nextTier.label}</div>
                <div className="text-xs text-muted-foreground">mancano {nextTier.threshold - count} inviti validi</div>
              </div>
            )}
          </div>

          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {tiers.map((t, i) => <TierRow key={t.threshold} t={t} count={count} i={i}/>)}
          </div>

          {early_access && (
            <div className="mt-6 rounded-xl border border-[#8dd8ff]/40 bg-[#8dd8ff]/10 p-3 text-sm flex items-center gap-2" data-testid="referral-early-access">
              <Trophy size={15} className="text-[#8dd8ff]"/> Accesso anticipato attivo: sarai tra i primi a provare le prossime funzioni AI di AgriNexus.
            </div>
          )}
          <CreditsPanel credits={credits || []} mine={myListings} onUse={useCredit}/>
        </div>
      </div>
    </div>
  );
}
